/**
 * Occupancy board API (C2 / FT1) — binds the FROZEN contract §1–§2.
 *
 * Backend (org resolved server-side from the session; both routes behind the
 * IPD admission gate by construction):
 *
 *   GET  /api/organisation/occupancy/board                → board payload (§1)
 *   POST /api/organisation/occupancy/beds/:bed_id/state   → manual transition (§2)
 *
 * The board is ONE read (inventory ⟕ BEDSTATE joined server-side) — the dept
 * filter is CLIENT-side, so there is NO query fan-out. Every function returns
 * `{ success, data?, error?, code? }`; `code` carries the server's machine
 * error_code (BED_STATE_CONFLICT / BLOCK_REASON_REQUIRED / BED_NOT_FOUND /
 * PERMISSION_DENIED) so the popover can self-correct on a 409 race.
 */

import apiClient from './apiClient';

const BOARD = '/api/organisation/occupancy/board';
const enc = encodeURIComponent;

/**
 * Extract the contract's machine `error_code` from the `{detail:{error_code,…}}`
 * envelope; falls back to a status-keyed friendly message. `current_state` (409)
 * is passed through so the popover can re-sync the chip from the server truth.
 */
function parseOccupancyError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.error_code) {
    return {
      error: detail.message || fallback,
      code: detail.error_code,
      ...(detail.current_state != null ? { current_state: detail.current_state } : {}),
    };
  }
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to change bed states.', code: 'PERMISSION_DENIED' };
    case 404: return { error: 'That bed no longer exists.', code: 'BED_NOT_FOUND' };
    default:  return { error: fallback };
  }
}

/** GET the whole board (§1). No params — dept filter is client-side. */
export const getOccupancyBoard = async () => {
  try {
    const response = await apiClient.get(BOARD);
    const d = response.data || {};
    return {
      success: true,
      data: {
        wards: d.wards || [],
        rooms: d.rooms || [],
        beds: d.beds || [],
        as_of: d.as_of || null,
      },
    };
  } catch (error) {
    console.error('[OccupancyAPI] getBoard error:', error);
    return { success: false, ...parseOccupancyError(error, 'Failed to load the occupancy board.') };
  }
};

/**
 * POST a manual housekeeping transition (§2). Body carries ONLY the three
 * contract fields — never echoes any derived/read-only bed field (strip-readonly
 * rule; extra=forbid would 422). `reason` is sent ONLY for a BLOCKED target.
 *
 * @param {string} bedId
 * @param {{target_state:string, expected_state:string, reason?:string}} args
 */
export const postBedState = async (bedId, { target_state, expected_state, reason } = {}) => {
  try {
    const body = { target_state, expected_state };
    if (target_state === 'BLOCKED') body.reason = reason;
    const response = await apiClient.post(
      `/api/organisation/occupancy/beds/${enc(bedId)}/state`,
      body,
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[OccupancyAPI] postBedState error:', error);
    return { success: false, ...parseOccupancyError(error, 'Failed to change the bed state.') };
  }
};
